import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useContext, useState } from 'react';
import { GlobalContext } from 'providers/Global';
import { ModalType } from '../../utils/constants';
import { changePassword, BadKeyError } from '../../signing/KeyManager';
import {
  getKeystore,
  saveKeystore,
  getKeystoreMeta,
  saveKeystoreMeta,
} from '../../utils/networkConfigStore';

const ChangePasswordModal = () => {
  const {
    modals: { [`${ModalType.ChangePasswordModal}`]: modalState },
    closeModal,
  } = useContext(GlobalContext);

  const open = modalState?.open ?? false;
  const networkId = (modalState as any)?.networkId ?? '';
  const accountId = (modalState as any)?.accountId ?? '';
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    closeModal(ModalType.ChangePasswordModal);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setError('');

    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    const base64 = getKeystore(networkId, accountId);
    if (!base64) {
      setError('Keystore not found');
      return;
    }

    setIsLoading(true);
    try {
      const meta = getKeystoreMeta(networkId, accountId);
      const newBase64 = await changePassword(base64, currentPassword, newPassword, meta?.keyType);
      saveKeystore(networkId, accountId, newBase64);

      if (meta) {
        saveKeystoreMeta(networkId, accountId, {
          ...meta,
          updatedAt: Date.now(),
        });
      }

      toast({ title: 'Password changed successfully', status: 'success', duration: 3000 });
      handleClose();
    } catch (err: any) {
      if (err instanceof BadKeyError) {
        setError('Incorrect password');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>Change Keystore Password</ModalHeader>
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Current Password</FormLabel>
              <Input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>New Password</FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Confirm New Password</FormLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </FormControl>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            <Button
              colorScheme="blue"
              w="full"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Changing password..."
            >
              Change Password
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ChangePasswordModal;
